<template >
  <div name="resourceadd" class="-mx-3 -mt-4">
    <section v-if="errored" class="w-full text-center mt-6">
      <h3 class="font-bold text-xl text-action-red">
        There was an issue adding the resource details.
      </h3>
      <p class="text-action-blue">
        Please try again shortly or contact us to resolve the issue.
      </p>
    </section>

    <div class="w-full mx-auto p-6 pb-10 bg-white rounded-xl z-10">
      <section class="w-full text-center mt-2" v-if="isSaving || isSuccess">
        <div class="progress-circle__container">
          <span class="progress-circle__percent font-bold text-lg"
            >{{ completedPercentage }}%</span
          >

          <svg
            class="progress-circle"
            viewBox="0 0 106 106"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
          >
            <g
              id="Page-1"
              stroke="none"
              stroke-width="1"
              fill="none"
              fill-rule="evenodd"
            >
              <g id="ProgressBar" transform="translate(-17.000000, -17.000000)">
                <circle
                  id="Oval"
                  stroke="#949494"
                  stroke-width="7"
                  fill-rule="nonzero"
                  cx="70"
                  cy="70"
                  r="50"
                ></circle>
                <path
                  class="progress-circle__path"
                  d="M70,120 C97.6142375,120 120,97.6142375 120,70 C120,42.3857625 97.6142375,20 70,20 C42.3857625,20 20,42.3857625 20,70 C20,97.6142375 42.3857625,120 70,120 Z"
                  id="Oval-Copy"
                  stroke="#1F254A"
                  stroke-width="5"
                  :stroke-dasharray="circle"
                  fill-rule="nonzero"
                  transform="translate(70.000000, 70.000000) rotate(-125.000000) translate(-70.000000, -70.000000) "
                ></path>
              </g>
            </g>
          </svg>
        </div>
        <p>Uploading file</p>
      </section>

      <section v-if="isResourceComplete" class="w-full text-center mt-2">
        <h3 class="font-bold text-xl text-action-blue">
          Your file has been uploaded.
        </h3>
        <p class="text-action-blue">
          The uploaded resource can be viewed within the documents library.
        </p>
        <button
          @click="refreshUpload()"
          type="button"
          class="publish-button hover:bg-blue-300 mt-4"
        >
          Upload another document
        </button>
      </section>

      <form
        class="space-y-2 px-4"
        v-on:submit.prevent="submitUpload"
        ref="form"
        v-if="isInitial"
      >

        <div class="grid grid-cols-1 space-y-2">
          <label class="text-sm font-bold text-gray-600 text-left tracking-wide"
            >Resource Title</label
          >

  
          <input
            class="p-3 mb-5 bg-white border border-gray-200 rounded h-36 focus:bg-white focus:outline-none"
            type=""
            v-model="title"
            placeholder="Enter title..."
          />
        </div>

        <div
          class="grid grid-cols-1 space-y-2 pt-3"
          v-if="displayKey != undefined"
        >
          <label class="text-sm font-bold text-gray-600 text-left tracking-wide"
            >Resource Type</label
          >

          <div class="flex flex-wrap mb-8" v-if="!isLibrary">
            <button
              type="button"
              class="mr-3 mb-2 rounded p-2 px-6 cursor-pointer hover:bg-gray-400 hover:bg-opacity-50 hover:text-action-blue font-bold icon fa-check"
              v-bind:class="[
                selectedFormat === 'document'
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-200 text-gray-600',
              ]"
              @click="selectedFormat = 'document'"
            >
              Document
            </button>

            <button
              type="button"
              class="mr-3 mb-2 rounded p-2 px-6 cursor-pointer hover:bg-gray-400 hover:bg-opacity-50 hover:text-action-blue font-bold icon fa-check"
              v-bind:class="[
                selectedFormat === 'diagram'
                  ? 'bg-gray-500 text-white'
                  : 'bg-gray-200 text-gray-600',
              ]"
              @click="selectedFormat = 'diagram'"
            >
              Diagram
            </button>
          </div>
        </div>
        <div class="grid grid-cols-1 space-y-3 pt-4">
          <label class="text-sm font-bold text-gray-600 tracking-wide"
            >Attach Document</label
          >
          <p v-if="hasFile" class="font-bold text-red-400">
            Selected file {{ file.name }}
          </p>
          <div
            class="flex items-center justify-center w-full"
            v-on:click="showUpload()"
          >
            <label
              class="flex flex-col rounded-lg border-4 border-dashed w-full h-48 p-10 group text-center cursor-pointer"
            >
              <div
                class="h-full w-full text-center flex flex-col items-center justify-center items-center"
              >
                <div class="flex flex-auto max-h-32 w-2/5 mx-auto -mt-10">
                  <img
                    class="has-mask h-32 object-center p-2 mt-6"
                    src="/static/images/empty/document.png"
                    alt="Add document resource"
                  />
                </div>
                <p class="pointer-none text-gray-500">
                  <span class="text-sm">Click to select</span> a file from your
                  computer
                </p>
              </div>
              <input
                type="file"
                class="hidden"
                v-on:change="handleFileUpload()"
                ref="file"
              />
            </label>
          </div>
        </div>

        <div>
          <button
            @click="submitForm()"
            type="submit"
            class="publish-button hover:opacity-50 my-5 w-full flex justify-center p-4 tracking-wide focus:outline-none focus:shadow-outline shadow-lg cursor-pointer transition ease-in duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!hasFile"
          >
            Upload
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ResourceService } from "@/services/resource.service";
import { fileResourceMixin } from "@/mixins/file-ResourceMixin.js";

export default {
  name: "resourceadd",
  mixins: [fileResourceMixin],
  props: {
    course: { type: Object, required: false },
    lesson: { type: Object, required: false },
    displayKey: { type: String, required: false },
  },
  computed: {
    ...mapGetters({
      currentUser: "currentUser",
      currentAccess: "currentAccess",
      settings: "settings",
    }),
    hasFile() {
      return this.file.name != undefined && this.file.name.length > 3;
    },
    circle() {
      return this.completedPercentage; //((this.completedPercentage / 100) * 100 * Math.PI) + ',9999';
    },
    isContentAdmin() {
      return this.currentUser.isContentAdmin;
    },
    isLibrary() {
      return this.course === undefined
    }
  },
  data() {
    return {
      school: this.$store.getters.school,
      errored: false,
      isResourceComplete: false,

      title: "",
      file: {},
      selectedFormat: "document",
     
    };
  },
  created() {
    //this.fetchOutcomes();
  },
  mounted() {

  },
  methods: {
    handleFileUpload() {
      this.file = this.$refs.file.files[0];
      console.log(this.file);
    },
    submitUpload() {
      let formData = new FormData();
      formData.append("file", this.file);
      var subfolder = this.isContentAdmin ? "useful" : "general";
      if (this.isLibrary) {
        subfolder = ""
      }
    
      const result = this.uploadDocument(this.selectedFormat + "s", formData, subfolder );

      result.then((response) => {
        console.log("complete Resource up", response);
        this.postUploadFileData();
      });
    },
    postUploadFileData() {
      let url = this.isContentAdmin ? this.fileName : this.remoteName;
      let typeId = this.isContentAdmin ? 1 : this.currentUser.typeID;
      let folder = this.isContentAdmin ? "useful" : "general";
      let key = this.displayKey != undefined ? this.displayKey : "document"

      var vm = this;
      var payload = {
        format: this.selectedFormat,
        display_key: key,
        folder: folder,
        type_id: typeId,
        display_order: 0,
        is_active: true,
        title: this.title,
        url: url,
      };

      if (this.lesson != undefined) {
        payload.resource_lessons = [
          {
            lesson_id: this.lesson.id,
          },
        ];
      } else if (this.course != undefined) {
        payload.resource_units = [
          {
            course_id: this.course.id,
          },
        ];
      }

      ResourceService.add(payload)
        .then((response) => {
          vm.completion();
        })
        .catch((err) => {
          this.failure(err);
        });
    },
    setFile(value) {
      this.fileUrl = value.file;
      this.type = value.type;
    },
    submitForm() {
      if (this.$refs.form.requestSubmit === undefined) {
        this.submitUpload();
      } else {
        this.$refs.form.requestSubmit();
      }
    },
    completion(response) {
      this.isResourceComplete = true;
      this.$emit("resource-added");
      window.scrollTo(0, 0);
    },
    failure(error) {
      this.isResourceComplete = false;
      this.errored = true;
      console.log("Resource update error " + error);
    },
    showUpload() {
      var input = document.createElement("input");
      input.type = "file";
      input.onchange = (e) => {
        this.file = e.target.files[0];
      };
      input.click();
    },
  },
  components: {},
};
</script>

<style scoped>
.section-head {
  @apply text-xl m-5 ml-0 font-bold text-orange-500;
}

.boxer {
  @apply mb-8 bg-gray-100 py-3 rounded border border-gray-300;
}

.boxer-header {
  @apply block uppercase tracking-wide text-blue-700 text-sm font-bold mb-3;
}
</style>